import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { CookieConsentModule } from './modules/cookieconsent/cookieconsent.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { appReduceMap } from './store/app.reduce-map';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { AuthEffects } from './store/effects/auth.effects';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DevicesEffects } from './store/effects/devices.effects';
import { RouterModule } from '@angular/router';
import { NavigationModule } from './angular-core/components/navigation/navigation.module';
import { HeaderComponent } from './shared/components/header/header.component'
import { SaveSheetService } from './angular-core/services/save-sheet.service';
import { FooterComponent } from './shared/components/footer/footer.component';
import { TranslateLoader, TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { ApiModule as IdentityManagementApiModule, Configuration as IdentityManagementConfiguration } from './generated/CrossPoint.IdentityManagement.Server';
import { ApiModule as RemoteXpacApiModule, Configuration as RemoteXpacConfiguration } from './generated/CrossPoint.RemoteXpac.Server';
import { ApiModule as LicenseApiModule, Configuration as LicenseConfiguration } from './generated/CrossPoint.License.Server';
import { ApiModule as SalesApiModule, Configuration as SalesConfiguration } from './generated/CrossPoint.Sales.Server';
import { ApiModule as XpacProxyApiModule, Configuration as XpacProxyConfiguration } from './generated/CrossPoint.XpacProxy.Server';
import { ApiModule as ProductionToolingApiModule, Configuration as ProductionToolingConfiguration } from './generated/CrossPoint.ProductionTooling.Server';
import { ApiModule as AnalyticsApiModule, Configuration as AnalyticsConfiguration } from './generated/CrossPoint.Analytics.Server';
import { ApiModule as InventoryManagementApiModule, Configuration as InventoryManagementConfiguration } from './generated/CrossPoint.InventoryManagement.Server';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TimeoutInterceptor, DEFAULT_TIMEOUT } from './shared/interceptors/time-out-Interceptor';
import { MoreModule } from './modules/more/more.module';
import { LicensesModule } from './modules/licenses/licenses.module';
import { devicesReducer } from './store/reducers/devices.reducer';
import { licensesReducer } from './store/reducers/licenses.reducer';
import { LicensesEffects } from './store/effects/licenses.effects';
import { salesReducer } from './store/reducers/sales.reducer';
import { SalesEffects } from './store/effects/sales.effect';
import { NewsModule } from './modules/news/news.module';
import { LocalizedDatePipe } from './angular-core/utils/pipes/localized-date/localized-date.pipe';

import { identityManagementReducer } from './store/reducers/identitymanagment.reducer';
import { IdentityManagementEffects } from './store/effects/identitymanagement.effects';
import { XpacProxyEffects } from './store/effects/xpacProxy.effects';
import { xpacProxyReducer } from './store/reducers/xpacProxy.reducer';
import { NotifierModule } from './angular-core/components/notifier/notifier.module';
import { LocalizedDateModule } from './angular-core/utils/pipes/localized-date/localized-date.pipe.module';
import { DatePipe } from '@angular/common';
import { ServiceModule } from './modules/service/service.module';
import { productionToolingReducer } from './store/reducers/production.reducer';
import { ProductionToolingEffects } from './store/effects/production.effects';
import { analyticsReducer } from './store/reducers/analytics.reducer';
import { AnalyticsEffects } from './store/effects/analytics.effects';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StringInterceptor } from './angular-core/interceptors/string.interceptor';
import { TrimInterceptor } from './angular-core/interceptors/trim.interceptor';
import { inventoryManagementReducer } from './store/reducers/inventorymanagement.reducer';
import { InventoryManagementEffects } from './store/effects/inventorymanagement.effects';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@Injectable()
export class ConsoleErrorHandler implements ErrorHandler {
  constructor(
    private ai: ApplicationinsightsAngularpluginErrorService,
  ) {
  }

  public handleError(error: any): void {
    console.error(error);
    this.ai.handleError(error);
  }
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatProgressBarModule,
    MatCheckboxModule,
    AppRoutingModule,
    RouterModule,
    NavigationModule,
    CookieConsentModule,
    HttpClientModule,
    MatIconModule,
    NewsModule,
    LicensesModule,
    MoreModule,
    ServiceModule,
    NotifierModule,
    LocalizedDateModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.remoteXpacApiUrl, environment.licenseApiUrl, environment.inventoryMgmtApiUrl, environment.salesApiUrl, environment.identityMgmtApiUrl, environment.xpacProxyUrl, environment.productionApiUrl, environment.analyticsApiUrl],
        sendAccessToken: true
      }
    }),
    StoreModule.forRoot(appReduceMap, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    StoreModule.forFeature('devices', devicesReducer),
    EffectsModule.forFeature([DevicesEffects]),
    StoreModule.forFeature('licenses', licensesReducer),
    EffectsModule.forFeature([LicensesEffects]),
    StoreModule.forFeature('sales', salesReducer),
    EffectsModule.forFeature([SalesEffects]),
    StoreModule.forFeature('xpacProxy', xpacProxyReducer),
    EffectsModule.forFeature([XpacProxyEffects]),
    StoreModule.forFeature('identityManagement', identityManagementReducer),
    EffectsModule.forFeature([IdentityManagementEffects]),
    StoreModule.forFeature('productionTooling', productionToolingReducer),
    EffectsModule.forFeature([ProductionToolingEffects]),
    StoreModule.forFeature('analytics', analyticsReducer),
    EffectsModule.forFeature([AnalyticsEffects]),
    StoreModule.forFeature('inventoryManagement', inventoryManagementReducer),
    EffectsModule.forFeature([InventoryManagementEffects]),
    EffectsModule.forRoot([AuthEffects, DevicesEffects]),
    !environment.production ? StoreDevtoolsModule.instrument({connectInZone: true}) : [],
    RemoteXpacApiModule.forRoot((): RemoteXpacConfiguration => new RemoteXpacConfiguration({ basePath: environment.remoteXpacApiUrl })),
    IdentityManagementApiModule.forRoot((): IdentityManagementConfiguration => new IdentityManagementConfiguration({ basePath: environment.identityMgmtApiUrl })),
    LicenseApiModule.forRoot((): LicenseConfiguration => new LicenseConfiguration({ basePath: environment.licenseApiUrl })),
    SalesApiModule.forRoot((): SalesConfiguration => new SalesConfiguration({ basePath: environment.salesApiUrl })),
    XpacProxyApiModule.forRoot((): XpacProxyConfiguration => new XpacProxyConfiguration({ basePath: environment.xpacProxyUrl })),
    ProductionToolingApiModule.forRoot((): ProductionToolingConfiguration => new ProductionToolingConfiguration({ basePath: environment.productionApiUrl })),
    AnalyticsApiModule.forRoot((): AnalyticsConfiguration => new AnalyticsConfiguration({ basePath: environment.analyticsApiUrl })),
    InventoryManagementApiModule.forRoot((): InventoryManagementConfiguration => new InventoryManagementConfiguration({ basePath: environment.inventoryMgmtApiUrl })),
  ],
  providers: [
    SaveSheetService,
    TranslateService,
    LocalizedDatePipe,
    DatePipe,
    TranslatePipe,
    
    [{ provide: ErrorHandler, useClass: ConsoleErrorHandler }],
    [{ provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true }],
    [{ provide: HTTP_INTERCEPTORS, useClass: StringInterceptor, multi: true }],
    [{ provide: HTTP_INTERCEPTORS, useClass: TrimInterceptor, multi: true }],
    [{ provide: DEFAULT_TIMEOUT, useValue: 35000 }],
    [{ provide: OAuthStorage, useFactory: () => new PrefixedStorage('portal_') }]
  ],
  bootstrap: [
    AppComponent
  ]
})
export class AppModule {
}

class PrefixedStorage implements OAuthStorage {
  constructor(private readonly prefix: string) {
  }

  getItem(key: string): string {
    return sessionStorage.getItem(this.prefix + key);
  }
  removeItem(key: string): void {
    sessionStorage.removeItem(this.prefix + key);
  }
  setItem(key: string, data: string): void {
    sessionStorage.setItem(this.prefix + key, data);
  }
}